import React, { useState, useEffect } from "react";
import { MdMonetizationOn, MdStars } from "react-icons/md";
import { FaCaretUp, FaCaretDown } from "react-icons/fa";
import useUserData from "../Hooks/useUserData";

// Función avanzada para formatear saldo COP
const formatSaldoCOP = (valor) => {
  if (valor === undefined || valor === null) return "0";

  // Para valores negativos
  const isNegative = valor < 0;
  const absValor = Math.abs(valor);

  // Millones (≥ 1,000,000)
  if (absValor >= 1000000) {
    const millones = absValor / 1000000;
    const formattedValue =
      millones % 1 === 0
        ? `${Math.floor(millones)}M`
        : `${millones.toFixed(1)}M`.replace(".0M", "M");
    return isNegative ? `-${formattedValue}` : formattedValue;
  }

  // Miles (≥ 1,000)
  if (absValor >= 1000) {
    const miles = absValor / 1000;
    const formattedValue =
      miles % 1 === 0
        ? `${Math.floor(miles)}K`
        : `${miles.toFixed(1)}K`.replace(".0K", "K");
    return isNegative ? `-${formattedValue}` : formattedValue;
  }

  // Valores menores a 1000
  return isNegative ? `-${absValor}` : absValor.toString();
};

export default function MyBalance() {
  const { userData, loading, error } = useUserData();
  const [isHovered, setIsHovered] = useState(false);
  const [prevSaldo, setPrevSaldo] = useState(null);
  const [saldoDirection, setSaldoDirection] = useState(null); // 'up', 'down', o null

  const saldo = userData?.saldo || 0;
  const formattedSaldo = formatSaldoCOP(saldo);
  const isVip = userData?.typeU === "Cuenta Vip";

  // Detectar cambios en el saldo para mostrar efectos
  useEffect(() => {
    if (prevSaldo !== null && saldo !== prevSaldo) {
      setSaldoDirection(saldo > prevSaldo ? "up" : "down");
      const timer = setTimeout(() => setSaldoDirection(null), 1500);
      return () => clearTimeout(timer);
    }

    if (prevSaldo === null && saldo !== null) {
      setPrevSaldo(saldo);
    }
  }, [saldo, prevSaldo]);

  // Actualizar prevSaldo después de la comparación
  useEffect(() => {
    if (saldo !== prevSaldo && !loading) {
      setPrevSaldo(saldo);
    }
  }, [saldo, loading]);

  if (loading)
    return (
      <div className="flex items-center space-x-2 px-3 py-1.5 rounded-md bg-gray-800 bg-opacity-50">
        <MdMonetizationOn className="text-gray-400 text-lg animate-pulse" />
        <span className="text-gray-300 text-xs">Cargando</span>
      </div>
    );

  if (error)
    return (
      <div className="flex items-center space-x-2 px-3 py-1.5 rounded-md bg-red-900 bg-opacity-30">
        <MdMonetizationOn className="text-red-400 text-lg" />
        <span className="text-red-300 text-xs">Error</span>
      </div>
    );

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center space-x-2 px-3 py-1.5 rounded-md bg-gray-800 bg-opacity-60 hover:bg-opacity-80 transition-all cursor-pointer">
        {isVip ? (
          <MdStars
            className={`text-yellow-400 text-lg ${
              isHovered ? "animate-pulse" : ""
            }`}
          />
        ) : (
          <MdMonetizationOn
            className={`text-yellow-400 text-lg ${
              isHovered ? "animate-pulse" : ""
            }`}
          />
        )}

        <div className="flex flex-col">
          {isVip ? (
            <>
              <span className="text-gray-400 text-xs font-medium">Estado</span>
              <span className="text-yellow-300 text-sm font-bold">VIP</span>
            </>
          ) : (
            <>
              <span className="text-gray-400 text-xs font-medium">Saldo</span>
              <div className="flex items-center">
                {saldoDirection === "up" && (
                  <FaCaretUp className="text-green-500 text-xs mr-1 animate-pulse" />
                )}
                {saldoDirection === "down" && (
                  <FaCaretDown className="text-red-500 text-xs mr-1 animate-pulse" />
                )}

                <span
                  className={`text-white text-sm font-bold ${
                    saldoDirection === "up"
                      ? "text-green-400"
                      : saldoDirection === "down"
                      ? "text-red-400"
                      : ""
                  }`}
                >
                  ${formattedSaldo}
                </span>

                <span className="text-gray-400 text-xs ml-1">COP</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
